import type { BauerImageProps } from './bauer-image-types'
const isClient = !(import.meta.env.SSR)

export function createImageFromKnownSize (width: number, height: number, image: BauerImageProps) {
  const maxWidth = 2000 // Max width that the image service supports
  const maxHeight = 3600 // Max height that the image service supports

  const aspectRatio = width / height
  if (isClient) {
    console.log('createImageFromKnownSize', width, height, image, aspectRatio)
  }

  // Clamp dimensions only if they exceed the maximum allowed size
  const isLandscape = aspectRatio > 1
  const clampedWidth = Math.min(width, isLandscape ? maxWidth : Math.round(maxHeight * aspectRatio))

  return createImgSrc(image, clampedWidth)
}

export function createImgSrc (props: BauerImageProps, width: number) {
  if (props.image?.externalImageSrc) {
    return new URL(props.image.externalImageSrc)
  }
  if (!props.image?.imageId || !props.brand) {
    return null
  }

  const cropping = getCropping(props.image, props.srcSetDefinition)

  return createImgUrl(props, width, cropping)
}

export function createImgUrl (props: BauerImageProps, width: number, cropping?: any) {
  const seoName = props.image?.seoName || 'image'
  if (props.image?.externalImageSrc) {
    return new URL(props.image.externalImageSrc)
  }

  const path = seoName +
    ',id=' + props.image?.imageId +
    ',b=' + props.brand +
    ',w=' + width +
    (cropping ? createCroppingUrlParams(cropping) : '') +
    ',rm=sk' + // rm=resizeMode, sk=Only Shrink Larger Images (shrink). Another commonly used option is to restrict IM so that it will only shrink images to fit into the size given. Never enlarge!
    getImageExtension(props.image?.mimetype || '')

  return new URL(path, props.imageServiceUrl)
}

function createImgSrcSets (props: BauerImageProps) {
  const srcSets = getImgDefinitions(props.brand, props.imageSrcDefinitions, props.setName)?.srcSet

  if (!srcSets) return []

  return srcSets.map(srcSet => ({
    src: createImgUrl(props, srcSet.width, getCropping(props.image, srcSet)).href,
    viewportWidth: srcSet.viewportWidth
  }))
}

export function createImgSrcSetArray (props: BauerImageProps) {
  return createImgSrcSets(props)
}

export const setMediaQuery = (viewport) => '(max-width: ' + viewport + 'px)'

export function getImgAltText (image: BauerImageProps, fallbackAltText = '') {
  return ((image?.imageAltText?.length > 0 ? image.imageAltText : fallbackAltText) + (image?.imageCredit?.length > 0 ? ` - Foto: ${image.imageCredit}` : '')).replace(/['"]+/g, '')
}

export function getImgTitle (image: BauerImageProps, fallbackAltText = '') {
  return image?.imageTitle?.length > 0 ? image.imageTitle : getImgAltText(image, fallbackAltText)
}

export function getCropping (obj, srcSetDefinition) {
  if (!obj) return undefined

  return obj.crop || (obj.croppings && srcSetDefinition?.format && obj.croppings[srcSetDefinition.format])
}

function createCroppingUrlParams (crop) {
  return (crop && crop.x1 && crop.y1 && crop.x2 && crop.y2) ? `,ca=${crop.x1},${crop.y1},${crop.x2},${crop.y2}` : ''
}

export function getImgDefinitions (brand, imageSrcDefinitions, setName) {
  if (!imageSrcDefinitions) {
    console.error('no image src definitions for brand \'' + brand + '\'')
    return
  }
  let definitions = imageSrcDefinitions.definitions.defaults
  if (setName) {
    if (imageSrcDefinitions.definitions[setName]) {
      definitions = imageSrcDefinitions.definitions[setName]
    } else if (isClient) {
      console.log(`[WARN] No set found for ${setName} - falling back to default. To fix: add definition to imageSrcDefinitions in rsConfig.`)
    }
  }
  return definitions
}

function getImageExtension (mimeType: BauerImageProps['image']['mimetype']) {
  return mimeType === 'image/gif' ? '.gif' : '.webp'
}

/**
 * Determines if an image is portrait based on dimensions
 */
export function isPortraitImage (width: number, height: number): boolean {
  return width > 0 && height > 0 && height >= width
}
